import React, { useEffect, useRef, useState } from 'react'
import './navbar.css';
import { MobileNav, ThemeSwitch } from '..';
import { motion } from 'framer-motion';
import { Clock, Coin, LogoVertical } from '../../assets';
import GlowButton from '../GlowButton';

const Navbar = (props) => {

  const puzzleDropdownRef = useRef(null);
  const puzzleButtonRef = useRef(null);
  const playDropdownRef = useRef(null);
  const playButtonRef = useRef(null);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (puzzleDropdownRef.current && !puzzleDropdownRef.current.contains(event.target)) {
        props.setIsPuzzleDropdownOpen(false);
      }
      if (playDropdownRef.current && !playDropdownRef.current.contains(event.target)) {
        props.setIsPlayDropdownOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [puzzleDropdownRef, playDropdownRef]);
  
  // Create separate dropdown elements outside the nav component
  const [puzzleDropdownContainer, setPuzzleDropdownContainer] = useState(null);
  const [playDropdownContainer, setPlayDropdownContainer] = useState(null);
  
  useEffect(() => {
    // Create a new div for the puzzle dropdown
    const puzzleContainer = document.createElement('div');
    puzzleContainer.id = 'puzzle-dropdown-container';
    puzzleContainer.style.position = 'fixed';
    puzzleContainer.style.zIndex = '9999';
    puzzleContainer.style.top = '0';
    puzzleContainer.style.left = '0';
    
    // Create a new div for the play dropdown
    const playContainer = document.createElement('div');
    playContainer.id = 'play-dropdown-container';
    playContainer.style.position = 'fixed';
    playContainer.style.zIndex = '9999';
    playContainer.style.top = '0';
    playContainer.style.left = '0';
    
    // Append them to the body
    document.body.appendChild(puzzleContainer);
    document.body.appendChild(playContainer);
    setPuzzleDropdownContainer(puzzleContainer);
    setPlayDropdownContainer(playContainer);
    
    // Clean up on unmount
    return () => {
      if (document.body.contains(puzzleContainer)) {
        document.body.removeChild(puzzleContainer);
      }
      if (document.body.contains(playContainer)) {
        document.body.removeChild(playContainer);
      }
    };
  }, []);
  
  // Position the puzzle dropdown
  useEffect(() => {
    if (props.isPuzzleDropdownOpen && puzzleButtonRef.current && puzzleDropdownContainer) {
      const buttonRect = puzzleButtonRef.current.getBoundingClientRect();
      
      // Create dropdown content
      const dropdownContent = document.createElement('div');
      dropdownContent.className = 'puzzle-dropdown-content';
      dropdownContent.style.position = 'fixed';
      dropdownContent.style.top = `${buttonRect.bottom + 5}px`;
      dropdownContent.style.left = `${buttonRect.left}px`;
      dropdownContent.style.width = '224px';
      dropdownContent.style.padding = '8px';
      dropdownContent.style.borderRadius = '6px';
      dropdownContent.style.background = 'rgba(255, 255, 255, 0.1)';
      dropdownContent.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.2)';
      dropdownContent.style.backdropFilter = 'blur(8px)';
      // @ts-ignore
      dropdownContent.style.WebkitBackdropFilter = 'blur(8px)';
      dropdownContent.style.border = '1px solid rgba(255, 255, 255, 0.18)';
      
      // Clear previous content
      puzzleDropdownContainer.innerHTML = '';
      
      // Add menu items
      const menuItems = [
        { icon: '🧩', text: 'Rated Puzzles', url: 'https://analysis.chessmind.io/puzzles?mode=rated' },
        { icon: '🏁', text: 'Puzzle Race', url: 'https://analysis.chessmind.io/puzzles' },
    //    { icon: '📅', text: 'Daily Puzzle', url: 'https://analysis.chessmind.io/puzzles?mode=daily' },
        { icon: '🎨', text: 'Puzzle Themes', url: 'https://analysis.chessmind.io/puzzles?mode=themes' },
        { icon: '🏆', text: 'Leaderboard', url: 'https://analysis.chessmind.io/leaderboard' },
      ];
      
      menuItems.forEach(item => {
        const menuItem = document.createElement('a');
        menuItem.className = 'puzzle-dropdown-item';
        menuItem.style.display = 'block';
        menuItem.style.padding = '12px 16px';
        menuItem.style.marginBottom = '4px';
        menuItem.style.borderRadius = '4px';
        menuItem.style.color = (props.selected !== 0 && !props.dark) ? 'black' : 'white';
        menuItem.style.fontSize = '14px';
        menuItem.style.cursor = 'pointer';
        menuItem.style.transition = 'all 0.2s ease';
        
        menuItem.innerHTML = `<span style="display: flex; align-items: center;"><span style="margin-right: 8px;">${item.icon}</span>${item.text}</span>`;
        
        menuItem.addEventListener('mouseover', () => {
          menuItem.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          menuItem.style.transform = 'scale(1.02)';
        });
        
        menuItem.addEventListener('mouseout', () => {
          menuItem.style.backgroundColor = 'transparent';
          menuItem.style.transform = 'scale(1)';
        });
        
        menuItem.addEventListener('click', () => {
          window.location.href = item.url;
          props.setIsPuzzleDropdownOpen(false);
        });
        
        dropdownContent.appendChild(menuItem);
      });
      
      // Add to container
      puzzleDropdownContainer.appendChild(dropdownContent);
    } else if (!props.isPuzzleDropdownOpen && puzzleDropdownContainer) {
      // Clear dropdown when closed
      puzzleDropdownContainer.innerHTML = '';
    }
  }, [props.isPuzzleDropdownOpen, puzzleDropdownContainer, props.dark, props.selected]);

  // Position the play dropdown
  useEffect(() => {
    if (props.isPlayDropdownOpen && playButtonRef.current && playDropdownContainer) {
      const buttonRect = playButtonRef.current.getBoundingClientRect();
      
      // Create dropdown content
      const dropdownContent = document.createElement('div');
      dropdownContent.className = 'play-dropdown-content';
      dropdownContent.style.position = 'fixed';
      dropdownContent.style.top = `${buttonRect.bottom + 5}px`;
      dropdownContent.style.left = `${buttonRect.left}px`;
      dropdownContent.style.width = '224px';
      dropdownContent.style.padding = '8px';
      dropdownContent.style.borderRadius = '6px';
      dropdownContent.style.background = 'rgba(255, 255, 255, 0.1)';
      dropdownContent.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.2)';
      dropdownContent.style.backdropFilter = 'blur(8px)';
      // @ts-ignore
      dropdownContent.style.WebkitBackdropFilter = 'blur(8px)';
      dropdownContent.style.border = '1px solid rgba(255, 255, 255, 0.18)';
      
      // Clear previous content
      playDropdownContainer.innerHTML = '';
      
      // Add menu items
      const menuItems = [
        { icon: '🤖', text: 'Play Computer', url: 'https://analysis.chessmind.io/play?mode=computer' },
        { icon: '👥', text: 'Play Online', url: 'https://analysis.chessmind.io/play' },
      ];
      
      menuItems.forEach(item => {
        const menuItem = document.createElement('a');
        menuItem.className = 'play-dropdown-item';
        menuItem.style.display = 'block';
        menuItem.style.padding = '12px 16px';
        menuItem.style.marginBottom = '4px';
        menuItem.style.borderRadius = '4px';
        menuItem.style.color = (props.selected !== 0 && !props.dark) ? 'black' : 'white';
        menuItem.style.fontSize = '14px';
        menuItem.style.cursor = 'pointer';
        menuItem.style.transition = 'all 0.2s ease';
        
        menuItem.innerHTML = `<span style="display: flex; align-items: center;"><span style="margin-right: 8px;">${item.icon}</span>${item.text}</span>`;
        
        menuItem.addEventListener('mouseover', () => {
          menuItem.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          menuItem.style.transform = 'scale(1.02)';
        });
        
        menuItem.addEventListener('mouseout', () => {
          menuItem.style.backgroundColor = 'transparent';
          menuItem.style.transform = 'scale(1)';
        });
        
        menuItem.addEventListener('click', () => {
          window.location.href = item.url;
          props.setIsPlayDropdownOpen(false);
        });
        
        dropdownContent.appendChild(menuItem);
      });
      
      // Add to container
      playDropdownContainer.appendChild(dropdownContent);
    } else if (!props.isPlayDropdownOpen && playDropdownContainer) {
      // Clear dropdown when closed
      playDropdownContainer.innerHTML = '';
    }
  }, [props.isPlayDropdownOpen, playDropdownContainer, props.dark, props.selected]);

  function formatTime(val) {
    const seconds = val / 1000;
    var minutes = parseInt(seconds / 60);
    const secondVal = parseInt(seconds % 60);
    var hours = parseInt(minutes / 60);
    minutes < 10 && (minutes = '0' + minutes);
    hours < 10 && (hours = '0' + hours);
    return hours + ':' + minutes + ':' + secondVal;
  }

  return (
   <> 
   {props.width <= 1170 && <MobileNav selected={props.selected} dark={props.dark} time={props.time} coin={props.coin} logged={props.logged} />}
   <div style={{zIndex: 100, display: props.selected === 1 ? 'block' : 'none'}} className='absolute top-0 right-0 w-full h-[10px] bg-gray-500 rounded' />
   <motion.div style={{zIndex: 110, display: props.width < 1170 ? 'none' : 'flex' }} animate={{translateY: (props.scrollPos < 700 || props.scrolledUp || props.selected === 1) ? 0 : -100}} transition={{duration: 0.3}} className={`${props.selected !== 1 ? 'h-[80px]' : 'opacity-0 hover:opacity-[1] h-[10px] hover:h-[60px]'} navbar_container`}>
      <div className='navbar_logo cursor-pointer' onClick={() => (window.location.href = '/')}>
          <img src={LogoVertical} width={200}/>
      </div>
      <div className='navbar_links'>
          <a style={{color: (props.selected !== 0 && !props.dark) ? 'black' : 'white'}} className={`navlink `} onClick={() => {
            window.location.href = '/';
          }}>Home</a>
          <a style={{color: (props.selected !== 0 && !props.dark) ? 'black' : 'white'}} className='navlink' onClick={() => {
              window.location.href = 'https://analysis.chessmind.io';
          }}>Analysis</a>
          <a style={{color: (props.selected !== 0 && !props.dark) ? 'black' : 'white'}} className='navlink' onClick={() => {
              window.location.href = 'https://chessmind.io/openings';
          }}>Openings</a>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <a 
              ref={playButtonRef}
              style={{color: (props.selected !== 0 && !props.dark) ? 'black' : 'white'}} 
              className='navlink flex items-center cursor-pointer' 
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.setIsPlayDropdownOpen(prevState => !prevState);
                props.setIsPuzzleDropdownOpen(false);
              }}
            >
              Play
              <svg 
                className={`ml-1 w-4 h-4 transition-transform duration-300 ${props.isPlayDropdownOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </a>
          </div>
          {/* Puzzles dropdown */}
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <a 
              ref={puzzleButtonRef}
              style={{color: (props.selected !== 0 && !props.dark) ? 'black' : 'white'}} 
              className='navlink flex items-center cursor-pointer' 
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.setIsPuzzleDropdownOpen(prevState => !prevState);
                props.setIsPlayDropdownOpen(false);
              }}
            >
              Puzzles
              <svg 
                className={`ml-1 w-4 h-4 transition-transform duration-300 ${props.isPuzzleDropdownOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </a>
          </div>
          {!props.logged && <>
            <a style={{color: (props.selected !== 0 && !props.dark) ? 'black' : 'white'}} className='navlink' onClick={() => {
              window.location.href = '/pricing';
          }}>Pricing</a>
          <a style={{color: (props.selected !== 0 && !props.dark) ? 'black' : 'white'}} className='navlink' onClick={() => {
              window.location.href = '/about';
          }}>About</a>
            <a style={{color: (props.selected !== 0 && !props.dark) ? 'black' : 'white'}} className='navlink' onClick={() => {
              window.location.href = '/contact';
          }}>Contact</a>
          </>}
          {props.logged && <><a style={{color: (props.selected !== 0 && !props.dark) ? 'black' : 'white'}} className='navlink' onClick={() => {
              window.location.href = '/settings';
          }}>Settings</a>
          <a style={{color: (props.selected !== 0 && !props.dark) ? 'black' : 'white'}} className='navlink' onClick={() => {
              window.location.href = '/settings?query=coins';
          }}>Coins</a>
         
          <a style={{color: (props.selected !== 0 && !props.dark) ? 'black' : 'white'}} className='navlink' onClick={() => {
            localStorage.removeItem('chesscloud-token');
            document.cookie = `chesscloud-token=; domain=.chessmind.io; path=/; max-age=-99999999`;
            window.location.href = '/';
          }}>Logout</a>
          {props.selected !== 0 && <ThemeSwitch setDark={props.setDark} dark={props.dark} />}
          </>}
      </div>
      {props.logged ? <><div className='flex items-center justify-center gap-3 w-[80px] cursor-pointer mr-[30px] ml-[10px]'>
          <img src={Coin} width={30} height={30}/>
          <a className={(props.selected !== 0 && !props.dark) ? 'text-black' : 'text-white'} onClick={() => {
            
          }}>{props.coin.toFixed(2)}</a>
          </div>
          <div className='flex items-center justify-between w-[120px] mr-[20px] cursoir-pointer'>
          <Clock />
          <a className={(props.selected !== 0 && !props.dark) ? 'text-black' : 'text-white'} onClick={() => {
            
          }}>{formatTime(props.time)}</a>
          </div></> : <>
          <GlowButton onClick={() => {window.location.href = '/login'}} text="Sign In"/>
          <GlowButton onClick={() => {window.location.href = '/register'}} text="Sign Up"/>
          </>}
    </motion.div>
      
    </>
  )
}

export default Navbar

